import React, { useRef } from 'react'
import { CheckCircle, PingPong } from '@phosphor-icons/react'
import { Icon } from '@shopflo/ui'
import { useDynamicSegment } from 'src/lib/contexts/DynamicSegmentContext'
import { useLocale } from 'src/lib/hooks/useLocal'
import produce from 'immer'
import { DynamicSegmentFormErrorStateType } from 'src/lib/types/segment'

interface SegmentNameEditorProps { }

const SegmentNameEditor: React.FC<SegmentNameEditorProps> = ({
}) => {
  const { segmentFormState, formErrorState, nameErrorKey, actions } = useDynamicSegment()
  const { setFormErrorState } = actions
  const { t } = useLocale()
  const didPlaceNameCaret = useRef(false)

  const handleNameFocus = (event: React.FocusEvent<HTMLTextAreaElement>) => {
    if (didPlaceNameCaret.current) return
    didPlaceNameCaret.current = true
    const field = event.currentTarget
    if (!field.value) return
    const end = field.value.length
    field.setSelectionRange(end, end)
  }

  return (
    <div className='flex flex-col gap-2'>
      {/* Dynamic Badge */}
      <div className='flex items-center gap-1'>
        <Icon IconComponent={PingPong} size='xs' className='text-[#009689]' iconProps={{ weight: 'fill' }} />
        <span className='text-text-sm-medium text-[#009689]'>{t('segment_creation.dynamic')}</span>
      </div>

      <div className='flex flex-col gap-1'>
        {/* Name Input */}
        <textarea
          key={nameErrorKey}
          rows={2}
          maxLength={36}
          placeholder='Title'
          value={segmentFormState?.name ?? ''}
          onChange={e => {
            if (formErrorState.name.status) {
              setFormErrorState(produce(formErrorState, (draft: DynamicSegmentFormErrorStateType) => {
                draft.name.status = false
                draft.name.message = ''
              }))
            }
            actions.setSegmentName(e.target.value)
          }}
          autoFocus
          onFocus={handleNameFocus}
          className={`min-h-0 w-full min-w-0 resize-none break-words bg-transparent text-text-lg-semibold text-zinc-800 outline-none ring-0 border-none focus:ring-0 [field-sizing:content] ${formErrorState.name.status ? 'animate-wiggle placeholder:text-red-300' : 'placeholder:text-zinc-400/70'}`}
        />

        {/* Description Input */}
        <textarea
          rows={2}
          maxLength={80}
          placeholder={t('segment_creation.description_placeholder')}
          value={segmentFormState?.description ?? ''}
          onChange={e => {
            actions.setSegmentDescription(e.target.value)
          }}
          className='min-h-0 w-full min-w-0 resize-none break-words bg-transparent text-text-sm-regular text-zinc-500 outline-none ring-0 border-none focus:ring-0 placeholder:text-zinc-400/70 [field-sizing:content]'
        />

        {/* Animated error reveal */}
        <div className={`grid transition-all duration-200 ease-in-out ${formErrorState.name.status ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'}`}>
          <div className='overflow-hidden'>
            <span className='text-text-xs-medium text-red-600'>{formErrorState.name.message}</span>
          </div>
        </div>
      </div>
    </div>
  )
}

export default SegmentNameEditor
